import { adminHttp as client } from "@shared/lib/http";
import { isRealBackendMode } from "@shared/lib/backendMode";
import { parseApiError } from "@shared/lib/errors";
import {
  botProtectionActions,
  botProtectionHeaders,
} from "@shared/lib/botProtection";
import { translateUi } from "@i18n/config";
import { loadAuth } from "@shared/lib/tokenStorage";

export type LoginResponse = {
  accessToken: string;
  refreshToken?: string | null;
  user: {
    id: string;
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
    permissions?: string[];
  };
  // optional expiry in seconds
  expiresIn?: number;
  storeId?: string | null;
  tenantId?: string | null;
};

type NotificationPreferences = {
  marketing?: boolean;
  product?: boolean;
};

type ProfileFieldErrorKey = "name" | "avatarUrl" | "locale" | "timezone";
type PasswordFieldErrorKey = "current" | "new" | "confirm";

export type UpdateMeError = Error & {
  status?: number;
  fieldErrors?: Partial<Record<ProfileFieldErrorKey, string>>;
};

export type ChangePasswordError = Error & {
  status?: number;
  fieldErrors?: Partial<Record<PasswordFieldErrorKey, string>>;
};

function normalizeProfileFieldErrorKey(
  input: string,
): ProfileFieldErrorKey | null {
  const lastSegment = input.split(".").pop() ?? input;
  const normalized = lastSegment.replace(/^\$+/, "");
  const camel = normalized
    ? `${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`
    : normalized;
  if (
    camel === "name" ||
    camel === "avatarUrl" ||
    camel === "locale" ||
    camel === "timezone"
  ) {
    return camel;
  }
  return null;
}

function normalizePasswordFieldErrorKey(
  input: string,
): PasswordFieldErrorKey | null {
  const lastSegment = input.split(".").pop() ?? input;
  const normalized = lastSegment.replace(/^\$+/, "");
  const camel = normalized
    ? `${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`
    : normalized;
  if (camel === "current" || camel === "currentPassword") return "current";
  if (
    camel === "next" ||
    camel === "new" ||
    camel === "newPassword" ||
    camel === "password"
  )
    return "new";
  if (
    camel === "confirm" ||
    camel === "confirmPassword" ||
    camel === "confirmation"
  )
    return "confirm";
  return null;
}

export async function login(
  email: string,
  password: string,
  remember?: boolean,
): Promise<LoginResponse> {
  try {
    const headers = await botProtectionHeaders(botProtectionActions.adminLogin);
    const res = await client.post<LoginResponse>(
      "/auth/login",
      { email, password, rememberMe: remember },
      { headers },
    );
    return res;
  } catch (err) {
    if (isRealBackendMode()) {
      throw err instanceof Error
        ? err
        : new Error(translateUi("loginFailed", "Login failed"));
    }
    const message =
      err instanceof Error
        ? err.message
        : translateUi("loginFailed", "Login failed");
    throw new Error(message);
  }
}

export type RefreshResponse = {
  accessToken: string;
  refreshToken?: string | null;
  user?: {
    id: string;
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
    permissions?: string[];
  };
  expiresIn?: number;
  expiresAt?: number;
};

const pendingRefreshes = new Map<string, Promise<RefreshResponse>>();

export function refresh(refreshToken: string): Promise<RefreshResponse> {
  const stored = loadAuth();
  const key = JSON.stringify([stored?.tenantId, stored?.user?.id, refreshToken]);
  const pending = pendingRefreshes.get(key);
  if (pending) return pending;
  // Session hydration and route recovery may overlap (including StrictMode).
  // Both callers must consume the same single-use refresh result.
  const request = client.post<RefreshResponse>("/auth/refresh", { refreshToken }).finally(() => {
    if (pendingRefreshes.get(key) === request) pendingRefreshes.delete(key);
  });
  pendingRefreshes.set(key, request);
  return request;
}

// --- Registration & Account ---
export type RegisterInput = {
  storeName: string;
  email: string;
  name: string;
  password: string;
  inviteToken?: string;
};
export type RegisterResponse = LoginResponse & {
  requiresEmailVerification?: boolean;
};

export async function register(body: RegisterInput): Promise<RegisterResponse> {
  try {
    const headers = await botProtectionHeaders(
      botProtectionActions.adminRegister,
    );
    return await client.post<RegisterResponse>(
      "/auth/register",
      {
        StoreName: body.storeName,
        AdminEmail: body.email,
        AdminPassword: body.password,
        // StoreSlug is optional, backend generates it if missing
      },
      { headers },
    );
  } catch (err) {
    if (isRealBackendMode()) {
      throw err instanceof Error
        ? err
        : new Error(translateUi("registrationFailed", "Registration failed"));
    }
    const message =
      err instanceof Error
        ? err.message
        : translateUi("registrationFailed", "Registration failed");
    throw new Error(message);
  }
}

export async function resendVerification(
  email: string,
): Promise<{ ok: boolean }> {
  try {
    return await client.post<{ ok: boolean }>(
      "/admin/auth/resend-verification",
      { email },
    );
  } catch {
    if (isRealBackendMode())
      throw new Error(
        translateUi("verificationResendFailed", "Verification resend failed"),
      );
    return { ok: true };
  }
}

export async function verifyEmail(token: string): Promise<{ ok: boolean }> {
  try {
    return await client.post<{ ok: boolean }>("/admin/auth/verify", { token });
  } catch {
    if (isRealBackendMode())
      throw new Error(
        translateUi("emailVerificationFailed", "Email verification failed"),
      );
    return { ok: true };
  }
}

export async function getMe(): Promise<
  Partial<{
    id: string;
    email: string;
    name: string;
    avatarUrl?: string;
    locale?: string;
    timezone?: string;
    notifications?: NotificationPreferences;
  }>
> {
  try {
    return await client.get("/api/admin/me");
  } catch {
    if (isRealBackendMode())
      throw new Error(
        translateUi("failedToLoadProfile", "Failed to load profile"),
      );
    // mock current user
    return {
      id: "u1",
      email: "admin@example.com",
      name: "Admin User",
      locale: "en-US",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }
}

export async function updateMe(
  input: Partial<{
    name?: string;
    avatarUrl?: string;
    locale?: string;
    timezone?: string;
    notifications?: NotificationPreferences;
  }>,
): Promise<{ ok: boolean; user?: unknown }> {
  try {
    const response = (await client.patch("/api/admin/me", input)) as any;
    if (
      response &&
      typeof response === "object" &&
      ("ok" in response || "user" in response)
    ) {
      return { ok: response.ok !== false, user: response.user };
    }
    return { ok: true, user: response };
  } catch (err) {
    if (isRealBackendMode()) {
      const parsed = parseApiError(
        err,
        translateUi("failedToUpdateProfile", "Failed to update profile"),
      );
      const fieldErrors: Partial<Record<ProfileFieldErrorKey, string>> = {};
      const validationErrors = parsed.errors || {};
      Object.entries(validationErrors).forEach(([rawKey, messages]) => {
        const key = normalizeProfileFieldErrorKey(rawKey);
        if (!key) return;
        const first = Array.isArray(messages) ? messages[0] : messages;
        if (typeof first === "string" && first.trim().length > 0) {
          fieldErrors[key] = first;
        }
      });
      const error: UpdateMeError = new Error(
        parsed.message ||
          translateUi("failedToUpdateProfile", "Failed to update profile"),
      );
      error.status = parsed.status;
      if (Object.keys(fieldErrors).length > 0) {
        error.fieldErrors = fieldErrors;
      }
      throw error;
    }
    return { ok: true, user: input };
  }
}

export async function changePassword(
  current: string,
  next: string,
  confirm?: string,
): Promise<{ ok: boolean }> {
  // Basic mock validation
  if (current === next)
    throw new Error(
      translateUi("newPasswordMustDifferFromCurrent", "New password must differ from current"),
    );
  try {
    const payload: Record<string, string> = { current, next };
    if (typeof confirm === "string") {
      payload.confirm = confirm;
    }
    return await client.post("/api/admin/me/change-password", payload);
  } catch (err) {
    if (isRealBackendMode()) {
      const parsed = parseApiError(
        err,
        translateUi("failedToChangePassword", "Failed to change password"),
      );
      const fieldErrors: Partial<Record<PasswordFieldErrorKey, string>> = {};
      const validationErrors = parsed.errors || {};
      Object.entries(validationErrors).forEach(([rawKey, messages]) => {
        const key = normalizePasswordFieldErrorKey(rawKey);
        if (!key) return;
        const first = Array.isArray(messages) ? messages[0] : messages;
        if (typeof first === "string" && first.trim().length > 0) {
          fieldErrors[key] = first;
        }
      });

      const error: ChangePasswordError = new Error(
        parsed.message ||
          translateUi("failedToChangePassword", "Failed to change password"),
      );
      error.status = parsed.status;
      if (Object.keys(fieldErrors).length > 0) {
        error.fieldErrors = fieldErrors;
      }
      throw error;
    }
    return { ok: true };
  }
}

export async function forgotPassword(email: string): Promise<void> {
  const headers = await botProtectionHeaders(
    botProtectionActions.adminPasswordResetRequest,
  );
  await client.post("/auth/forgot-password", { email }, { headers });
}

export async function resetPassword(
  email: string,
  token: string,
  newPassword: string,
): Promise<void> {
  await client.post("/auth/reset-password", { email, token, newPassword });
}

export async function logout(refreshToken?: string | null): Promise<void> {
  const payload = refreshToken ? { refreshToken } : undefined;
  await client.post("/auth/logout", payload);
}
